// app/src/features/boot/useUrlHydration.js
import { useEffect, useRef } from "react";
import { noteDeepLinkOpen } from "../../hooks/useForecastOpenIntent.js";
import { resolveSelection } from "../../lib/compareUrl";
import { MAX_COMPARE_ROOTS } from "../../lib/comparison";
import { isTerminal, STATUS } from "../../lib/forecast/model.js";
import { branchLinkFocused, comparisonSet } from "../../lib/forecast/reducer.js";
import {
  selectFocusableForecastById,
  selectLegacyForecastById,
  selectBranchRootForecast,
  selectLinkedBranchRun,
  selectVisibleBranchRuns,
} from "../../lib/forecast/selectors.js";
import { unionDateRange } from "../../lib/laneLayout";
import { installUrlSync, readViewFromLocation } from "../../lib/view/urlSync.js";

/** The deep link's notice when `?forecast=` names nothing this viewer may see (SOO-862). */
export const LINK_NOT_OPENED_NOTICE =
  "Couldn't open this link: the forecast doesn't exist or isn't shared with you.";

/** Longest branch premise the notice quotes before cutting it with an ellipsis. */
const BRANCH_NAME_MAX = 90;

/**
 * The deep link's notice when `?forecast=` names a branch and its root was opened (SOO-862):
 * names the branch by its premise when it has one, and says when it was cleared, since a cleared
 * branch (or one under a cleared parent) is not drawn on the root's timeline (SOO-1008).
 *
 * @param {{ modification?: string | null }} branch
 * @param {{ cleared?: boolean }} [opts]
 * @returns {string}
 */
export function branchLinkNotice(branch, { cleared = false } = {}) {
  const name = (branch?.modification ?? "").trim();
  const what = cleared
    ? "a branch of this forecast that has been cleared"
    : "a branch of this forecast";
  if (!name) return `This link points to ${what}.`;
  const shown = name.length > BRANCH_NAME_MAX ? `${name.slice(0, BRANCH_NAME_MAX - 1)}…` : name;
  return `This link points to ${what}: “${shown}”.`;
}

/**
 * The URL→store hydration effects (the one-shot `?compare=` hydration and the one-shot
 * `?forecast=` deep link) and the state→URL mirror (`installUrlSync`, wired once both have run),
 * with the two "hydrated" refs the telemetry app-state reads. Must run before its next caller,
 * `useTelemetryAppStateWiring` (features/telemetry/), which takes the two hydrated refs; that
 * wiring's own place in the order is pinned by
 * `features/telemetry/__tests__/useTelemetryWiring.test.jsx`.
 *
 * The run store reaches this hook as `runStore` and its dispatch as `dispatchRun` (injected,
 * never imported — C-23); `handleSelectRef` is the log rows' forward ref.
 *
 * @param {object} deps
 * @param {object} deps.runStore  the run store (injected, never imported — C-23)
 * @param {Function} deps.dispatchRun  the store's dispatch (injected)
 * @param {{current: Function|null}} deps.handleSelectRef  the log rows' handleSelect
 * @param {(message: string) => void} deps.noticeShown  the transient notice strip's setter
 */
export function useUrlHydration({
  // both lanes' load signals and the loader (useBootWiring's outputs)
  forecastsLoaded,
  sharedSettled,
  linkClaim,
  loadedRef,
  claimLoadSlot,
  isCurrentLoadSlot,
  loadConversationThread,
  // the compare hydration's
  forecasts,
  forecastLiveProgressEnabled,
  dispatchRun,
  debugUI,
  setCompareMode,
  autoZoom,
  pushSayerMsg,
  // the deep link's
  handleSelectRef,
  noticeShown,
  // the store (the claim lookups, the URL mirror)
  runStore,
}) {
  // The URL→store hydration effects below set these true once they've read the inbound params,
  // and the state→URL mirror (installUrlSync) is only wired up afterwards, so the runs-loaded
  // burst can't clobber ?forecast=/?compare= before they're applied.
  const compareHydratedRef = useRef(false);

  useEffect(() => {
    if (compareHydratedRef.current) return;
    if (!forecastsLoaded) return; // gate on load finishing, NOT forecasts.length
    const wanted = readViewFromLocation().compareIds;
    if (wanted.length >= 2) {
      if (loadedRef.current) {
        // Resolve the URL compare ids against the FULL store (not the capped `forecasts`
        // display list), so an old shared ?compare= id — one older than FORECAST_DISPLAY_CAP —
        // still hydrates instead of being pruned.
        const store = runStore.getState();
        // findFc is cached per rid because each miss scans runsById and rebuilds the child index.
        const _fcById = new Map();
        const findFc = (rid) => {
          if (!_fcById.has(rid))
            _fcById.set(rid, selectLegacyForecastById(store, rid, forecastLiveProgressEnabled));
          return _fcById.get(rid);
        };
        const resolvable = wanted.map(findFc).filter(Boolean);
        const { rootIds, dropped } = resolveSelection(wanted, resolvable);
        const capped = rootIds.slice(0, MAX_COMPARE_ROOTS);
        if (capped.length >= 2) {
          // URL forecast ids are translated to runIds before dispatch.
          dispatchRun(comparisonSet(capped.map((rid) => findFc(rid)?.id).filter(Boolean)));
          // Only force compareMode ON when the debug UI is enabled — compare is a
          // debug-gated feature (the Dual-Engine checkbox lives under sooth-debugUI),
          // so a shared ?compare= URL must not bypass that gate for a non-debug user.
          // The selection is still restored, so enabling debug later surfaces it via
          // the checkbox without a reload.
          if (debugUI) setCompareMode(true);
          // Refit the x-scale to frame the compared lanes: an in-app ?compare hydration keeps the
          // current pan/zoom, which would leave the lanes off-scale. autoZoom to the LATEST
          // resolution across them (union max) so both lanes' resolution markers land on screen.
          // Snap (animate=false), since this is a restore, not a user gesture.
          const fitRoots = capped.map(findFc).filter(Boolean);
          const fitRange = unionDateRange(fitRoots);
          if (fitRange) autoZoom(fitRange.max.toISOString().slice(0, 10), false);
          // In-flight branches under BOTH compare roots are resumed by the unified
          // resume sweep: it iterates every kind:'branch' plan entry from the hydrated
          // store INDEPENDENT of which leg is focused, so a secondary root's branches
          // reconnect from their persisted server rows without a separate per-root call.
          // Claim a fresh load slot so any concurrent single-root restore (e.g. active-forecast
          // hydration racing this compare hydration) drops its stale result via the
          // isCurrentLoadSlot guard below, and vice versa.
          const compareLoadToken = claimLoadSlot();
          // Load each leg via the shared loader (claim:false — the batch claimed
          // the slot once above). Each ?compare= id is a forecastId; resolve it to
          // the run's forecastId (fetch) + conversationId/runId (store key).
          Promise.all(
            capped.map((rid) => {
              const f = findFc(rid);
              return loadConversationThread(f?.forecastId || rid, f?.id || rid, {
                token: compareLoadToken,
                claim: false,
              });
            }),
          ).then(() => {
            if (!isCurrentLoadSlot(compareLoadToken)) return;
            if (dropped.length)
              pushSayerMsg(
                `${dropped.length} compared forecast${dropped.length > 1 ? "s" : ""} no longer exist${dropped.length > 1 ? "" : "s"}.`,
                { kind: "notice" },
              );
          });
        } else if (dropped.length) {
          pushSayerMsg(
            `${dropped.length} compared forecast${dropped.length > 1 ? "s" : ""} no longer exist${dropped.length > 1 ? "" : "s"}.`,
            { kind: "notice" },
          );
        }
      } else {
        // A failed load applies the raw ?compare ids (capped) without pruning, notice or
        // thread-load. compareRoots resolves them once a real list arrives (an empty list leaves
        // compareOn false, so no broken render), which keeps URL sync un-wedged and preserves the
        // inbound selection. The store comparison selection is deferred: runIds cannot be resolved
        // without forecasts[] (clientId invariant), so the selection is not restored in the store,
        // the URL is preserved, and a later real hydration sets it.
        if (debugUI) setCompareMode(true);
      }
    }
    compareHydratedRef.current = true;
    // pushSayerMsg intentionally omitted from deps: it's a useCallback([active])
    // so its identity changes with `active`, but the one-shot gate guarantees this
    // body runs at most once — a stale closure can never be invoked twice, so the
    // changing identity is harmless and listing it would only risk a re-fire.
  }, [forecastsLoaded, forecasts, forecastLiveProgressEnabled]); // eslint-disable-line react-hooks/exhaustive-deps -- pushSayerMsg is omitted on purpose: the one-shot gate runs this body at most once (above)

  // Notification emails link to `/?forecast=<clientId>`, and the focused forecast is a
  // persistent, shareable param, so a reload or shared link restores focus. Once the log has
  // loaded, this focuses that forecast via the same handleSelect the log rows use (forwarded
  // through handleSelectRef). The param is NOT stripped — installUrlSync (wired below) keeps
  // ?forecast= in sync with the focused run (upgrading a client id to the shareable forecastId
  // once allocated). One-shot, gated on load finishing.
  //
  // Boot focus is split by run status: this effect restores focus only to a TERMINAL forecast the
  // URL names (completed/errored/cancelled). A RUNNING root the URL names is focused by the
  // resume sweep instead (useForecastResume `singleRootFocus` ← resolveSingleRootFocus), which
  // resumes it FOREGROUND (reattaches the live stream + owns active/phase/chat). PREPROCESSING is
  // covered by neither: planResume only plans STATUS.RUNNING, and it is a sub-second pre-run
  // phase with no resumable server stream, so there is nothing to restore. When the URL names no
  // running root, the sweep leaves focus null and the blank-home boot (passive resume) is
  // preserved.
  const forecastDeepLinkRef = useRef(false);
  useEffect(() => {
    if (forecastDeepLinkRef.current) return;
    if (!forecastsLoaded) return;
    // Wait for BOTH LANES, not just the owner leg: `forecastsLoaded` covers useBootForecasts only,
    // while a VIEWER's forecast exists solely in the shared lane, which loads on its own lifecycle.
    // This effect is one-shot (success or no-match both mark done), so firing it while the shared
    // lane is in flight would resolve against a store that lane has not filled yet, miss, and mark
    // itself done for good.
    if (!sharedSettled) return;
    const wanted = readViewFromLocation().activeId;
    if (wanted) {
      // Resolve against the FULL store, not the capped `forecasts` display list: a deep link can
      // point at a forecast older than FORECAST_DISPLAY_CAP, which `selectLegacyForecasts`
      // excludes. A link that names a BRANCH (MCP connector URLs and alert emails carry branch
      // ids) resolves to the root it belongs to: the UI has no "selected branch" a link could set,
      // so it opens the root, where the branch is drawn, and says so (SOO-862).
      const state = runStore.getState();
      const direct = selectFocusableForecastById(state, wanted);
      const branch = direct ? null : selectLinkedBranchRun(state, wanted);
      const f = branch ? selectBranchRootForecast(state, branch) : direct;
      // Skip if this forecast is ALREADY the focused one: handleSelect repurposes a click on an
      // already-active row to open its Tracking Settings, which is the wrong outcome for this
      // automated one-shot check. Read activeRunId live rather than assuming `active` here is
      // stale, since a sessionStorage-driven restore-select can land in the same render as this
      // effect's first eligible one.
      const alreadyFocused = Boolean(f) && state.activeRunId === f.id;
      // An OWNED running root is the resume sweep's to focus (it reattaches the stream). A SHARED
      // running root is not in the sweep, which considers owned rows only, so it is selected here
      // through handleSelect, which does not reattach a stream for a viewer
      // (shouldReattachOnSelect).
      const ownedRunning = Boolean(f) && f.status === STATUS.RUNNING && !!state.runsById[f.id];
      const selectable =
        Boolean(f) && (isTerminal(f.status) || (f.status === STATUS.RUNNING && !ownedRunning));
      if (selectable && handleSelectRef.current && !alreadyFocused) {
        noteDeepLinkOpen();
        handleSelectRef.current(f);
      }
      // The branch's root may be on a shared page not loaded yet: useBootWiring pages the
      // lane in and reports "paging" until the root arrives or the pages run out.
      if (branch && !f && linkClaim === "paging") return;
      if (branch) {
        // The branch is in the store, so there is nothing to claim: the one-shot is done. The
        // root was either just focused above or is the resume sweep's to focus
        // (resolveSingleRootFocus maps the branch id to it). Otherwise the root is not in the
        // store (a branch on a forecast no longer shared with the viewer): the link did not open.
        const opened =
          ownedRunning || (selectable && (alreadyFocused || Boolean(handleSelectRef.current)));
        // A cleared branch, or one under a cleared parent, is not drawn, so there is nothing to
        // bring into view: the notice says so instead (SOO-1008).
        const cleared =
          opened && !selectVisibleBranchRuns(state, f.id).some((r) => r.runId === branch.runId);
        noticeShown?.(opened ? branchLinkNotice(branch, { cleared }) : LINK_NOT_OPENED_NOTICE);
        // Expand the branch and have the forecast view bring it into view (SOO-1008).
        if (opened && !cleared) dispatchRun(branchLinkFocused(branch.runId));
        forecastDeepLinkRef.current = true;
        return;
      }
      // Not done yet if a claim is still the outstanding possibility: a link recipient's forecast
      // is in NEITHER lane until the claim lands, so the one-shot must not be marked done on that
      // first miss. Once the claim lands and the reload has hydrated the row, this effect runs
      // again and focuses it. A refused claim (or a claimed id that still resolves to nothing)
      // means the link names nothing this viewer may see: say so rather than silently leaving the
      // previous focus on screen.
      if (!f) {
        if (linkClaim === "pending" || linkClaim === "paging") return;
        noticeShown?.(LINK_NOT_OPENED_NOTICE);
      }
    }
    forecastDeepLinkRef.current = true;
    // handleSelectRef is a ref (stable); the one-shot gate makes re-fire impossible.
    // `sharedSettled` is a dep, not just a read: it is the edge that lets this
    // effect run at all for a viewer, so omitting it would leave the one-shot
    // waiting on a re-render it has no reason to get.
  }, [forecastsLoaded, forecasts, sharedSettled, linkClaim]); // eslint-disable-line react-hooks/exhaustive-deps -- handleSelectRef (a useRef), runStore (the store, an import at module scope), noticeShown and dispatchRun (identity-stable) arrive as hook inputs

  // Wire the view mirror + Back/Forward handler ONCE, and only AFTER both URL→store boot-applies
  // above have run, so the runs-loaded burst can't serialize an empty view over the inbound
  // ?forecast=/?compare= before they're read. From here on, focusing a forecast pushes a history
  // entry (Back/Forward navigate forecasts) and compare toggles replace; popstate re-applies the
  // URL's view via view-only actions (never touching a running forecast). Deps are
  // [forecastsLoaded, sharedSettled] (never `forecasts`): the body reads no other reactive value,
  // and keying on forecasts would tear down + reinstall the listeners on every list change.
  //
  // `sharedSettled` IS LOAD-BEARING HERE: with `[forecastsLoaded]` alone this would run once, bail
  // on a still-false `forecastDeepLinkRef`, and never re-run, so URL sync would silently never
  // install, taking Back/Forward and ?forecast= mirroring with it. It waits for the same
  // signals the deep-link effect does, so it can only run after it — `linkClaim` included: a link
  // that waits on its claim finishes the deep link only when the claim settles.
  const urlSyncRef = useRef(null);
  useEffect(() => {
    if (urlSyncRef.current) return;
    if (!forecastsLoaded) return;
    if (!compareHydratedRef.current || !forecastDeepLinkRef.current) return;
    urlSyncRef.current = installUrlSync({ store: runStore, window });
    return () => {
      urlSyncRef.current?.();
      urlSyncRef.current = null;
    };
  }, [forecastsLoaded, sharedSettled, linkClaim]); // eslint-disable-line react-hooks/exhaustive-deps -- runStore (the store, an import at module scope) arrives as a hook input

  return { compareHydratedRef, forecastDeepLinkRef };
}
